import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Input, Modal, Popconfirm, Space, Switch, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import {
  createExpenseCategory,
  deleteExpenseCategory,
  fetchExpenseCategories,
  updateExpenseCategory,
  type ExpenseCategoryItem,
} from './api';

const { Text } = Typography;

/**
 * The per-firm expense category nomenclature. Categories in use cannot be
 * deleted (FK Restrict -> 409) — deactivating hides them from new expenses
 * while history keeps its labels. SmartBill imports add entries here on
 * their own.
 */
export function CategoriiModal({ open, firmaId, onClose }: { open: boolean; firmaId: string; onClose: () => void }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const { data: categories, isLoading } = useQuery({
    queryKey: ['expense-categories', firmaId, 'all'],
    queryFn: () => fetchExpenseCategories(firmaId, true),
    enabled: open && !!firmaId,
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
  }

  function reportError(error: unknown, fallback: string) {
    message.error(getApiError(error)?.message ?? fallback);
  }

  const createMutation = useMutation({
    mutationFn: (name: string) => createExpenseCategory({ companyId: firmaId, name }),
    onSuccess: () => {
      setNewName('');
      refresh();
    },
    onError: (error) => reportError(error, 'Categoria nu a putut fi adăugată.'),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; name?: string; isActive?: boolean }) =>
      updateExpenseCategory(input.id, { name: input.name, isActive: input.isActive }),
    onSuccess: () => {
      setEditingId(null);
      refresh();
    },
    onError: (error) => reportError(error, 'Categoria nu a putut fi actualizată.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteExpenseCategory(id),
    onSuccess: () => refresh(),
    onError: (error) => reportError(error, 'Categoria este folosită de cheltuieli — dezactiveaz-o în loc să o ștergi.'),
  });

  const columns: ColumnsType<ExpenseCategoryItem> = [
    {
      title: 'Nume',
      key: 'name',
      render: (_, row) =>
        editingId === row.id ? (
          <Input
            size="small"
            value={editingName}
            autoFocus
            onChange={(event) => setEditingName(event.target.value)}
            onPressEnter={() => updateMutation.mutate({ id: row.id, name: editingName.trim() })}
          />
        ) : (
          <Text delete={!row.isActive}>{row.name}</Text>
        ),
    },
    {
      title: 'Activă',
      key: 'isActive',
      width: 90,
      render: (_, row) => (
        <Switch
          size="small"
          checked={row.isActive}
          onChange={(checked) => updateMutation.mutate({ id: row.id, isActive: checked })}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 190,
      render: (_, row) =>
        editingId === row.id ? (
          <Space size={4}>
            <Button size="small" type="primary" onClick={() => updateMutation.mutate({ id: row.id, name: editingName.trim() })}>
              Salvează
            </Button>
            <Button size="small" onClick={() => setEditingId(null)}>
              Renunță
            </Button>
          </Space>
        ) : (
          <Space size={4}>
            <Button
              size="small"
              onClick={() => {
                setEditingId(row.id);
                setEditingName(row.name);
              }}
            >
              Redenumește
            </Button>
            <Popconfirm
              title="Ștergi categoria?"
              description="Doar categoriile nefolosite pot fi șterse."
              okText="Șterge"
              cancelText="Renunță"
              onConfirm={() => deleteMutation.mutate(row.id)}
            >
              <Button size="small" danger>
                Șterge
              </Button>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  return (
    <Modal title="Categorii de cheltuieli" open={open} onCancel={onClose} footer={null} width={560} destroyOnHidden>
      <Space.Compact block style={{ marginBottom: 12 }}>
        <Input
          placeholder="Categorie nouă (ex. Combustibil)"
          value={newName}
          maxLength={120}
          onChange={(event) => setNewName(event.target.value)}
          onPressEnter={() => newName.trim() && createMutation.mutate(newName.trim())}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={createMutation.isPending}
          disabled={!newName.trim()}
          onClick={() => createMutation.mutate(newName.trim())}
        >
          Adaugă
        </Button>
      </Space.Compact>
      <Table<ExpenseCategoryItem>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={categories ?? []}
        loading={isLoading}
        pagination={false}
        locale={{ emptyText: 'Nicio categorie — se creează și automat la importul SmartBill.' }}
      />
    </Modal>
  );
}
